import { ResizeObserver } from '@juggle/resize-observer';
import classNames from 'classnames';
import React from 'react';
import ReactDOM from 'react-dom';
import smoothscroll from 'smoothscroll-polyfill';

import Icon, { ICON_TYPES } from '@commons/Icon';
import rowStyles from '@commons/Table/Row/row.scss';

import { isScrollFixForRTLMatrixEnabled } from '@services/featureFlags';

import styles from './scrollableContent.scss';

smoothscroll.polyfill();

export const SCROLL_DIRECTION = { LEFT: 'LEFT', RIGHT: 'RIGHT' };
const OVERFLOW_TO_SHOW_SCROLL = 5;
const SCROLL = 300;

interface ScrollableContentProps {
  children: React.ReactNode;
  className?: string;
}

interface ScrollableContentProps {
  isRTL?: boolean;
}

class ScrollableContent extends React.Component<ScrollableContentProps> {
  constructor(props) {
    super(props);
    if (isScrollFixForRTLMatrixEnabled() && props.isRTL) {
      this.state = {
        isInStart: false,
        isInEnd: true,
        hasOverflow: false
      };
    } else {
      this.state = {
        isInStart: true,
        isInEnd: false,
        hasOverflow: false
      };
    }
    this.onScroll = this.onScroll.bind(this);
    this.onArrowClick = this.onArrowClick.bind(this);
    this.setRef = this.setRef.bind(this);
    this.evaluateContentOverflow = this.evaluateContentOverflow.bind(this);
    this.contentSizeObserver = new ResizeObserver(this.evaluateContentOverflow);
  }

  componentDidMount() {
    // eslint-disable-next-line react/no-find-dom-node
    const node = ReactDOM.findDOMNode(this);
    const childrenRows = node.getElementsByClassName(rowStyles.row);

    // The observer should also be aware of the table rows within it.
    // The block below will make it recalc when these expand.
    // See: EEQ-95316.
    for (const row of childrenRows) {
      const { width, height } = row.getBoundingClientRect();
      const rowExists = width > 0 && height > 0;

      if (rowExists) {
        this.contentSizeObserver.observe(row);
      }
    }

    this.contentSizeObserver.observe(node);
  }

  componentWillUnmount() {
    this.contentSizeObserver.disconnect();
  }

  setRef(container) {
    this.$container = container;
  }

  onScroll({ target }) {
    if (!target || this.$container !== target) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.$container;
    const { isRTL } = this.props;
    const scrollableSpace = scrollWidth - clientWidth;

    if (isScrollFixForRTLMatrixEnabled() && isRTL) {
      this.setState({
        isInStart: scrollableSpace + scrollLeft < OVERFLOW_TO_SHOW_SCROLL,
        isInEnd: scrollLeft > OVERFLOW_TO_SHOW_SCROLL || scrollLeft === 0
      });
    } else {
      this.setState({
        isInStart: scrollLeft < OVERFLOW_TO_SHOW_SCROLL,
        isInEnd: scrollableSpace - scrollLeft < OVERFLOW_TO_SHOW_SCROLL
      });
    }
  }

  onArrowClick(direction) {
    if (!this.$container) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.$container;
    const { isRTL } = this.props;
    const scrollableSpace = scrollWidth - clientWidth;

    let position;

    if (isScrollFixForRTLMatrixEnabled() && isRTL) {
      position =
        direction === SCROLL_DIRECTION.LEFT
          ? Math.min(scrollableSpace, scrollLeft - (clientWidth - SCROLL))
          : Math.min(0, scrollLeft + (clientWidth - SCROLL));
    } else {
      position =
        direction === SCROLL_DIRECTION.LEFT
          ? Math.max(0, scrollLeft - (clientWidth - SCROLL))
          : Math.min(scrollableSpace, scrollLeft + (clientWidth - SCROLL));
    }

    this.$container.scrollTo({
      top: 0,
      left: position,
      behavior: 'smooth'
    });
  }

  evaluateContentOverflow() {
    if (!this.$container) return;
    const { hasOverflow: prevValue } = this.state;
    const { scrollWidth, clientWidth } = this.$container || {};
    const hasOverflow = scrollWidth > clientWidth;
    if (hasOverflow === prevValue) return;
    this.setState({ hasOverflow });
  }

  render() {
    const { children, className, isRTL } = this.props;
    const { isInStart, isInEnd, hasOverflow } = this.state;

    const mainContainerStyles = classNames(styles.mainContainer, className);
    const wrapperStyles = classNames(styles.contentWrapper, styles.overflowYVisibleWorkaround, {
      [styles.disableLeftIndicator]: !hasOverflow || isInStart,
      [styles.disableRightIndicator]: !hasOverflow || isInEnd
    });

    const scrollLeftButton = (
      <button
        className={`${styles.icon} ${styles.leftIcon}`}
        onClick={() => this.onArrowClick(SCROLL_DIRECTION.LEFT)}
        type="button"
        id={`leftIconButton`}
        disabled={!hasOverflow || isInStart}
        aria-label="Scroll Left"
      >
        <Icon type={ICON_TYPES.ARROW_DROPDOWN} containerStyles={styles.leftIconContent} />
      </button>
    );

    const scrollRightButton = (
      <button
        className={`${styles.icon} ${styles.rightIcon}`}
        onClick={() => this.onArrowClick(SCROLL_DIRECTION.RIGHT)}
        type="button"
        id={`rightIconButton`}
        aria-label="Scroll Right"
        disabled={!hasOverflow || isInEnd}
      >
        <Icon type={ICON_TYPES.ARROW_DROPDOWN} containerStyles={styles.rightIconContent} />
      </button>
    );

    return (
      <div className={mainContainerStyles}>
        <div className={wrapperStyles} onScroll={this.onScroll} ref={this.setRef}>
          {isRTL ? scrollRightButton : scrollLeftButton}
          {isRTL ? scrollLeftButton : scrollRightButton}
          {children}
        </div>
      </div>
    );
  }
}

export default ScrollableContent;
